<script lang="ts">
    import { onMount } from 'svelte';
    import { get, writable } from 'svelte/store';
    import ViewCard from '../components/ViewCard.svelte';
    import ViewForm from '../components/ViewForm.svelte';
    import type { ViewConfig } from '../services/view';
    import {
      getViews,
      updateView,
      deleteView,
    } from '../services/view';
    import ProjectTabs from '../components/ProjectTabs.svelte';
    import { getProject } from '../services/project';
    import { isAdmin, isSuperAdmin, currentUser} from '../stores/store';
    import { pageTitle } from '../stores/store';
    
    // The Project ID from the URL
    export let projectId: string;

    let url;
    let project;
    
    let views = writable<ViewConfig[]>([]);
    let filteredViews = writable<ViewConfig[]>([]);
    let loading = true;
    let error: string | null = null;
    let searchQuery = '';
    let sortBy: 'name' | 'created' | 'updated' = 'created';
    let sortOrder: 'asc' | 'desc' = 'desc';
    
    // Form state
    let showForm = false;
    let editingView: ViewConfig | null = null;
    let formTitle = 'Edit View';
    
    // Delete confirmation
    let showDeleteConfirm = false;
    let viewToDelete: ViewConfig | null = null;

    // Copy feedback
    let copyFeedback = '';
    let copyTimeout: number;
    
    // Current user permissions
    $: currentUserId = $currentUser._id;
    $: canEditAll = $isAdmin || $isSuperAdmin;
    
    // Filter and sort views
    $: {
      let filtered = $views.filter(view => {
        const searchLower = searchQuery.toLowerCase();
        const name = view.name || '';
        const description = view.description || '';
        const createdBy = view.createdBy || '';
        
        return (
          name.toLowerCase().includes(searchLower) ||
          description.toLowerCase().includes(searchLower) ||
          createdBy.toLowerCase().includes(searchLower)
        );
      });
      
      // Sort views
      filtered.sort((a, b) => {
        let comparison: number;
        switch (sortBy) {
          case 'name':
            comparison = (a.name || '').localeCompare(b.name || '');
            break;
          case 'created':
            const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            comparison = aCreated - bCreated;
            break;
          case 'updated':
            const aDate = a.updatedAt || a.createdAt;
            const bDate = b.updatedAt || b.createdAt;
            const aTime = aDate ? new Date(aDate).getTime() : 0;
            const bTime = bDate ? new Date(bDate).getTime() : 0;
            comparison = aTime - bTime;
            break;
          default:
            comparison = 0;
        }
        return sortOrder === 'asc' ? comparison : -comparison;
      });
      
      filteredViews.set(filtered);
    }
    
    onMount(async () => {
      try {
        project = await getProject(projectId);
        $pageTitle = `${project.name} - Views`;
        url = `${import.meta.env.VITE_SITE_PREFIX}${project.slug}/network`;
        await loadViews();
      } catch (err) {
        console.error('Failed to initialize gallery:', err);
        error = 'Failed to load project data';
      }
    });
    
    async function loadViews() {
      loading = true;
      error = null;
      
      try {
        const data = await getViews(projectId);
        // Ensure all views have unique IDs
        const viewsWithIds = data.map((view, index) => ({
          ...view,
          id: view.id || `view-${index}-${Date.now()}`,
          name: view.name || `View ${index + 1}`,
          createdAt: view.createdAt || new Date().toISOString(),
          createdBy: view.createdBy || 'Unknown',
        }));
        views.set(viewsWithIds);
      } catch (err) {
        console.error('Failed to load views:', err);
        error = 'Failed to load views. Please try again.';
        views.set([]);
      } finally {
        loading = false;
      }
    }
    
    function handleEdit(event: CustomEvent<ViewConfig>) {
      const view = event.detail;
      
      // Check permissions
      if (!canEditView(view)) {
        error = 'You do not have permission to edit this view';
        return;
      }
      
      editingView = view;
      formTitle = 'Edit View';
      showForm = true;
    }
    
    function handleDelete(event: CustomEvent<ViewConfig>) {
      const view = event.detail;
      
      // Check permissions
      if (!canEditView(view)) {
        error = 'You do not have permission to delete this view';
        return;
      }
      
      viewToDelete = view;
      showDeleteConfirm = true;
    }
    
    function handleViewNetwork(event: CustomEvent<ViewConfig>) {
      const view = event.detail;
      const networkUrl = `${import.meta.env.VITE_SITE_PREFIX}${project.slug}/network/view/${view.id}`;
      window.open(networkUrl, '_blank');
    }

    function handleCopyEmbed(event: CustomEvent<ViewConfig>) {
      const view = event.detail;
      const embedUrl = `${import.meta.env.VITE_SITE_PREFIX}${project.slug}/embed/view/${view.id}`;
      copyToClipboard(embedUrl, 'Embed link copied to clipboard!');
    }

    function handleCopyNetworkLink(event: CustomEvent<ViewConfig>) {
      const view = event.detail;
      const networkUrl = `${import.meta.env.VITE_SITE_PREFIX}${project.slug}/network/view/${view.id}`;
      copyToClipboard(networkUrl, 'Network link copied to clipboard!');
    }

    async function copyToClipboard(text: string, message: string) {
      try {
        await navigator.clipboard.writeText(text);
        showCopyFeedback(message);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
        error = 'Failed to copy to clipboard';
      }
    }

    function showCopyFeedback(message: string) {
      clearTimeout(copyTimeout);
      copyFeedback = message;
      copyTimeout = setTimeout(() => {
        copyFeedback = '';
      }, 3000);
    }
    
    async function handleSaveView(event: CustomEvent<{ 
      name: string; 
      description: string;
      adminLevel: 'admin' | 'facilitator' | 'none' | 'hidden';
    }>) {
      const { name, description, adminLevel } = event.detail;
      error = null;
      console.log('handleSaveview', event.detail);
      
      try {
        if (editingView) {
          const updated = await updateView(projectId, editingView.id, {
            viewData: {  // Wrap in viewData
              name,
              description,
              adminLevel
            }
          });
          
          views.update(items => 
            items.map(item => item.id === editingView!.id ? updated : item)
          );
        }
        
        showForm = false;
        editingView = null;
      } catch (err) {
        console.error('Failed to save view:', err);
        error = 'Failed to update view. Please try again.';
      }
    }
    
    function handleCancelForm() {
      showForm = false;
      editingView = null;
    }
    
    async function confirmDelete() {
      if (!viewToDelete) return;
      
      error = null;
      
      try {
        await deleteView(projectId, viewToDelete.id);
        views.update(items => 
          items.filter(item => item.id !== viewToDelete!.id)
        );
        
        showDeleteConfirm = false;
        viewToDelete = null;
      } catch (err) {
        console.error('Failed to delete view:', err);
        error = 'Failed to delete view. Please try again.';
      }
    }
    
    function cancelDelete() {
      showDeleteConfirm = false;
      viewToDelete = null;
    }
    
    function canEditView(view: ViewConfig): boolean {
      return canEditAll || view.createdBy === currentUserId;
    }
    
    function handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        error = null;
        copyFeedback = '';
      }
    }
  </script>
  
  <svelte:window on:keydown={handleKeydown} />
  
  <ProjectTabs {projectId} />
  
  <div class="panel">
    <div class="panel-inner">
      <!-- Header -->
      <div class="gallery-header">
        <h1>Views</h1>
      </div>
      
      <!-- Copy Feedback -->
      {#if copyFeedback}
        <div class="copy-feedback-banner">
          <div class="feedback-content">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{copyFeedback}</span>
          </div>
        </div>
      {/if}
      
      <!-- Error Message -->
      {#if error}
        <div class="error-banner">
          <div class="error-content">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 15h.01" stroke="currentColor" stroke-width="2"/>
            </svg>
            <span>{error}</span>
          </div>
          <button class="error-close" on:click={() => error = null}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      {/if}
      
      <!-- Search and Filter Controls -->
      <div class="controls">
        <div class="search-box">
          <input 
            type="text" 
            placeholder="Search views..." 
            bind:value={searchQuery}
            class="search-input"
          />
        </div>
        
        <div class="sort-controls">
          <select bind:value={sortBy} class="sort-select">
            <option value="created">Created Date</option>
            <option value="updated">Updated Date</option>
            <option value="name">Name</option>
          </select>
          
          <button 
            class="sort-order-btn" 
            on:click={() => sortOrder = sortOrder === 'asc' ? 'desc' : 'asc'}
            title="Toggle sort order"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {#if sortOrder === 'asc'}
                <path d="M7 14l5-5 5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              {:else}
                <path d="M7 10l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              {/if}
            </svg>
          </button>
        </div>
      </div>
      
      <!-- Loading State -->
      {#if loading}
        <div class="loading-container">
          <div class="loading-spinner">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-dasharray="60" stroke-dashoffset="60">
                <animateTransform attributeName="transform" type="rotate" values="0 12 12;360 12 12" dur="1s" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
          <p>Loading views...</p>
        </div>
      {:else if $filteredViews.length === 0}
        <!-- Empty State -->
        <div class="empty-state">
          {#if searchQuery}
            <h2>No views found</h2>
            <p>No views match your search criteria.</p>
            <button class="btn btn-secondary" on:click={() => searchQuery = ''}>
              Clear Search
            </button>
          {:else}
            <h2>No views yet</h2>
            <p>No views have been created for this project.</p>
          {/if}
        </div>
      {:else}
        <!-- View Grid -->
        <div class="view-grid">
          {#each $filteredViews as view, index (view.id || index)}
            <ViewCard 
              {view} 
              {projectId}
              canEdit={canEditView(view)}
              canDelete={canEditView(view)}
              on:edit={handleEdit}
              on:delete={handleDelete}
              on:viewNetwork={handleViewNetwork}
              on:copyEmbed={handleCopyEmbed}
              on:copyNetworkLink={handleCopyNetworkLink}
            />
          {/each}
        </div>
      {/if}
    </div>
  </div>
  
  <!-- Forms and Modals -->
   {#if editingView}
  <ViewForm 
    isOpen={showForm}
    view={editingView}
    title={formTitle}
    on:save={handleSaveView}
    on:cancel={handleCancelForm}
  />
  {/if}
  
  <!-- Delete Confirmation Modal -->
  {#if showDeleteConfirm && viewToDelete}
    <div class="modal-backdrop" on:click={(e) => e.target === e.currentTarget && cancelDelete()}>
      <div class="modal-container delete-modal">
        <div class="modal-header">
          <h2>Delete View</h2>
          <button class="close-btn" on:click={cancelDelete}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="delete-icon">
          </div>
          <p>Are you sure you want to delete the view <strong>"{viewToDelete.name}"</strong>?</p>
          <p class="warning-text">This action cannot be undone.</p>
        </div>
        
        <div class="modal-footer">
          <button class="btn btn-secondary" on:click={cancelDelete}>
            Cancel
          </button>
          <button class="btn btn-danger" on:click={confirmDelete}>
            Delete
          </button>
        </div>
      </div>
    </div>
  {/if}
  
  <style>
    
    .gallery-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
    }
    
    .gallery-header h1 {
      margin: 0;
      font-size: 28px;
      font-weight: 700;
      color: #111827;
    }

    .copy-feedback-banner {
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 24px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    
    .feedback-content {
      display: flex;
      align-items: center;
      gap: 12px;
      color: #16a34a;
    }
    
    .error-banner {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    
    .error-content {
      display: flex;
      align-items: center;
      gap: 12px;
      color: #b91c1c;
    }
    
    .error-close {
      background: none;
      border: none;
      cursor: pointer;
      color: #b91c1c;
      padding: 4px;
      border-radius: 4px;
      transition: background-color 0.2s ease;
    }
    
    .error-close:hover {
      background: #fee2e2;
    }
    
    .controls {
      display: flex;
      gap: 16px;
      margin-bottom: 24px;
      flex-wrap: wrap;
      align-items: center;
    }
    
    .search-box {
      position: relative;
      flex: 1;
      min-width: 250px;
    }
    
    .search-box svg {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
    }
    
    .search-input {
      width: 100%;
      padding: 8px 12px 8px 40px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 14px;
      box-sizing: border-box;
    }
    
    .search-input:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    .sort-controls {
      display: flex;
      gap: 8px;
      align-items: center;
    }
    
    .sort-select {
      padding: 0px 10px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 14px;
      background: white;
    }
    
    .sort-order-btn {
      width: 40px;
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      background: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s ease;
    }
    
    .sort-order-btn:hover {
      background: #f3f4f6;
    }
    
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 64px 0;
      color: #6b7280;
    }
    
    .loading-spinner {
      margin-bottom: 16px;
    }
    
    .empty-state {
      text-align: center;
      padding: 64px 0;
      color: #6b7280;
    }
    
    .empty-state svg {
      margin-bottom: 24px;
      color: #d1d5db;
    }
    
    .empty-state h2 {
      margin: 0 0 12px 0;
      font-size: 20px;
      font-weight: 600;
      color: #374151;
    }
    
    .empty-state p {
      margin: 0 0 24px 0;
      color: #6b7280;
    }
    
    .view-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 24px;
    }
    
    .btn {
      padding: 8px 16px;
      border: none;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    
    .btn-primary {
        background-color: #fc72cd;
      color: white;
    }
    
    .btn-primary:hover {
        background-color: #fc72cd;
    }
    
    .btn-secondary {
      background: #f3f4f6;
      color: #374151;
      border: 1px solid #d1d5db;
    }
    
    .btn-secondary:hover {
      background: #e5e7eb;
    }
    
    .btn-danger {
      background: #dc2626;
      color: white;
    }
    
    .btn-danger:hover {
      background: #b91c1c;
    }
    
    /* Modal styles */
    .modal-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    
    .modal-container {
      background: white;
      border-radius: 8px;
      width: 100%;
      max-width: 500px;
      max-height: 90vh;
      overflow: hidden;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    }
    
    .delete-modal {
      max-width: 400px;
    }
    
    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      border-bottom: 1px solid #e0e0e0;
    }
    
    .modal-header h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #333;
    }
    
    .close-btn {
      width: 32px;
      height: 32px;
      border: none;
      background: none;
      cursor: pointer;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
      transition: background-color 0.2s ease;
    }
    
    .close-btn:hover {
      background: #f5f5f5;
      color: #333;
    }
    
    .modal-body {
      padding: 20px;
      text-align: center;
    }
    
    .delete-icon {
      color: #dc2626;
      margin-bottom: 16px;
    }
    
    .modal-body p {
      margin: 0 0 12px 0;
      color: #374151;
    }
    
    .warning-text {
      color: #b91c1c;
      font-size: 14px;
    }
    
    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      padding: 20px;
      border-top: 1px solid #e0e0e0;
      background: #f9fafb;
    }
  </style>